import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = '/api';

function SectionHeader({ num, title }) {
  return (
    <div className="sec-divider">
      <span className="sec-num">{num}</span>
      <div className="sec-line" />
      <span className="sec-title">{title}</span>
      <div className="sec-mark" />
    </div>
  );
}

function StatCard({ label, value, note, delay }) {
  return (
    <div
      className="anim-in"
      style={{
        background: "#fff",
        border: "2px solid #111",
        padding: "24px",
        position: "relative",
        animationDelay: delay,
      }}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Corner marks */}
      <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000" }} />
      <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000" }} />

      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "0.55rem",
        fontWeight: 700,
        letterSpacing: "0.2em",
        color: "#999",
        textTransform: "uppercase",
        marginBottom: 12,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "2.8rem",
        fontWeight: 700,
        color: "#111",
        lineHeight: 1,
        marginBottom: 12,
      }}>
        {String(value).padStart(3, "0")}
      </div>
      <div style={{ height: 1, background: "#111", marginBottom: 8 }} />
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "0.55rem",
        letterSpacing: "0.14em",
        color: "#bbb",
        textTransform: "uppercase",
      }}>
        {note}
      </div>
    </div>
  );
}

function SummaryRow({ summary, index }) {
  const date = new Date(summary.created_at).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
  return (
    <Link
      to={`/summary/${summary.id}`}
      style={{ textDecoration: "none" }}
      data-testid={`summary-card-${summary.id}`}
    >
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          borderBottom: "1px solid #e8e8e4",
          transition: "background 0.1s",
          cursor: "pointer",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#F5F5F3"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
      >
        {/* Row number */}
        <div style={{
          padding: "14px 16px",
          borderRight: "1px solid #e8e8e4",
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "0.6rem",
          color: "#CC0000",
          fontWeight: 700,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          minWidth: 48,
        }}>
          {String(index + 1).padStart(2, "0")}
        </div>
        {/* Thumbnail */}
        {summary.thumbnail_url && (
          <div style={{ padding: "10px 12px", flexShrink: 0, display: "flex", alignItems: "center" }}>
            <img
              src={summary.thumbnail_url}
              alt=""
              style={{ width: 72, height: 45, objectFit: "cover", border: "1px solid #ddd" }}
              onError={e => { e.target.style.display = "none"; }}
            />
          </div>
        )}
        {/* Content */}
        <div style={{ flex: 1, padding: "14px 16px", minWidth: 0 }}>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.72rem",
            fontWeight: 600,
            color: "#111",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginBottom: 4,
          }}>
            {summary.video_title.toUpperCase()}
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.58rem",
            color: "#888",
            letterSpacing: "0.08em",
          }}>
            {summary.channel_name} · {date}
          </div>
        </div>
        {/* Status */}
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {summary.notion_page_id && (
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.55rem",
              letterSpacing: "0.12em",
              color: "#fff",
              background: "#111",
              padding: "2px 6px",
              textTransform: "uppercase",
            }}>
              NOTION
            </span>
          )}
          <span style={{ color: "#CC0000", fontSize: "1rem", fontFamily: "monospace" }}>→</span>
        </div>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({ total_summaries: 0, total_channels: 0, saved_to_notion: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/stats`),
      axios.get(`${API}/summaries`),
    ]).then(([s, r]) => {
      setStats(s.data);
      setRecent(r.data.slice(0, 6));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="anim-in r-max-1400" style={{ maxWidth: 1100 }} data-testid="dashboard">

      {/* Page header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "0.6rem",
          letterSpacing: "0.2em",
          color: "#CC0000",
          textTransform: "uppercase",
          marginBottom: 6,
        }}>
          SYSTEM / CHANNEL-DIGEST / v1.0
        </div>
        <h1 style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "2rem",
          fontWeight: 700,
          color: "#111",
          letterSpacing: "-0.02em",
          margin: 0,
          lineHeight: 1,
        }}>
          DASHBOARD
        </h1>
        <div style={{ height: 3, background: "#111", marginTop: 10, width: 64 }} />
      </div>

      {/* Stats */}
      <div style={{ marginBottom: 32 }}>
        <SectionHeader num="01" title="METRICS" />
        <div className="stagger r-grid-1" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
          <StatCard label="TOTAL SUMMARIES" value={loading ? 0 : stats.total_summaries} note="DOCUMENTS PROCESSED" delay="0.03s" />
          <StatCard label="TRACKED CHANNELS" value={loading ? 0 : stats.total_channels} note="SOURCES INDEXED" delay="0.06s" />
          <StatCard label="SAVED TO NOTION" value={loading ? 0 : stats.saved_to_notion} note="PAGES CREATED" delay="0.09s" />
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ marginBottom: 32 }}>
        <SectionHeader num="02" title="QUICK ACCESS" />
        <div className="r-grid-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Link to="/summarize" data-testid="quick-summarize-btn" style={{ textDecoration: "none" }}>
            <div style={{
              background: "#CC0000",
              border: "2px solid #CC0000",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "background 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#AA0000"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#CC0000"; }}
            >
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.16em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>NEW DOCUMENT</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", fontWeight: 700, color: "#fff", letterSpacing: "0.1em" }}>SUMMARIZE VIDEO →</div>
              </div>
            </div>
          </Link>
          <Link to="/channels" data-testid="quick-channels-btn" style={{ textDecoration: "none" }}>
            <div style={{
              background: "#fff",
              border: "2px solid #111",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              transition: "background 0.12s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F5F5F3"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
            >
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.16em", color: "#bbb", marginBottom: 4 }}>MANAGE SOURCES</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", fontWeight: 700, color: "#111", letterSpacing: "0.1em" }}>EDIT CHANNELS →</div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent summaries */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div className="sec-divider" style={{ flex: 1, marginBottom: 0 }}>
            <span className="sec-num">03</span>
            <div className="sec-line" />
            <span className="sec-title">RECENT DOCUMENTS</span>
            <div className="sec-mark" />
          </div>
          <Link to="/history" data-testid="view-all-summaries-link" style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.6rem",
            letterSpacing: "0.14em",
            color: "#CC0000",
            textDecoration: "none",
            marginLeft: 16,
            flexShrink: 0,
          }}>
            VIEW ALL →
          </Link>
        </div>

        <div style={{ border: "2px solid #111", background: "#fff" }}>
          {/* Table header */}
          <div className="table-scroll-wrap">
          <div style={{ minWidth: 520 }}>
          <div style={{
            display: "flex",
            background: "#111",
            padding: "8px 0",
          }}>
            <div style={{ width: 48, padding: "0 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.12em" }}>#</div>
            <div style={{ width: 96, padding: "0 12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.12em" }}>THUMB</div>
            <div style={{ flex: 1, padding: "0 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.12em" }}>DOCUMENT TITLE / SOURCE</div>
            <div style={{ width: 120, padding: "0 16px", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", color: "#888", letterSpacing: "0.12em" }}>STATUS</div>
          </div>

          {loading ? (
            [1,2,3].map(i => (
              <div key={i} style={{ display: "flex", padding: "16px", borderBottom: "1px solid #e8e8e4", gap: 16, opacity: 0.4 }}>
                <div style={{ width: 32, height: 12, background: "#eee" }} />
                <div style={{ flex: 1, height: 12, background: "#eee" }} />
              </div>
            ))
          ) : recent.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", color: "#bbb", letterSpacing: "0.14em" }}>
                NO DOCUMENTS · PROCESS A VIDEO TO BEGIN
              </div>
            </div>
          ) : (
            recent.map((s, i) => <SummaryRow key={s.id} summary={s} index={i} />)
          )}
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}
